import { cx } from "./lib/cx";

/**
 * Shared Tailwind recipes.
 * Marker classes (`sc-ws`, `sc-btn`, …) remain only for :has() layout
 * and leftover descendant hooks.
 */

const focus = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";

export const ui = {
  shell: cx(
    "shell h-full bg-ds-bg-a font-sans text-ink [color-scheme:light]",
    "[&_input[type=checkbox]]:accent-primary [&_input[type=radio]]:accent-primary [&_progress]:accent-primary",
    "[&_.cad-viewer-root_button]:!border-grey-100 [&_.cad-viewer-root_button]:!bg-white [&_.cad-viewer-root_button]:!text-ink [&_.cad-viewer-root_button]:!shadow-none",
    "[&_.cad-viewer-root_button[aria-pressed=true]]:!border-primary [&_.cad-viewer-root_button[aria-pressed=true]]:!bg-primary [&_.cad-viewer-root_button[aria-pressed=true]]:!text-white",
  ),
  /** Top cell of the icon rail — brand mark sits here, icons start below. */
  topbarBand: "h-[71px] shrink-0 bg-grey-25",
  /** Icon rail column — always 61px on wide screens. */
  railColumn:
    "flex h-full min-h-0 w-[61px] shrink-0 flex-col max-[900px]:hidden",
  main: cx(
    "sc-main flex min-h-0 min-w-0 flex-1 flex-col gap-5 overflow-y-auto px-5 pb-5 max-[900px]:gap-3 max-[900px]:p-3",
    "has-[>[data-gutter=even]]:!gap-4 has-[>[data-gutter=even]]:!p-4",
    "has-[>.sc-ws]:!px-4 has-[>.sc-ws]:!pb-4",
    "has-[>.sc-cols]:overflow-hidden has-[>.sc-ai]:overflow-hidden has-[>.sc-ws]:overflow-hidden has-[>.sc-home]:overflow-hidden",
    "has-[>.sc-ws]:sticky has-[>.sc-ws]:top-0 has-[>.sc-ws]:box-border has-[>.sc-ws]:h-dvh",
    "has-[>.sc-ai]:sticky has-[>.sc-ai]:top-0 has-[>.sc-ai]:box-border has-[>.sc-ai]:h-dvh",
    "has-[>.sc-ws]:[&>.sc-topbar]:flex-none has-[>.sc-ws]:[&>.sc-journey]:flex-none",
    "has-[>.sc-ai]:[&>.sc-topbar]:flex-none",
  ),
  iconBtn: cx(
    "relative inline-flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-grey-100 bg-white",
    focus,
  ),
  avatar: cx(
    "inline-flex size-12 cursor-pointer items-center justify-center rounded-full border border-grey-100 bg-primary-wash text-base font-semibold text-primary",
    focus,
  ),
  pop: "absolute top-[calc(100%+8px)] right-0 z-20 w-[312px] rounded-lg border border-grey-100 bg-white p-3 shadow-xl",
  popHead: "m-0 mb-2 text-sm font-semibold text-ink",
  popSub: "m-0 mb-0.5 text-xs text-ink-muted [overflow-wrap:anywhere]",

  btn: cx(
    "sc-btn inline-flex h-12 shrink-0 cursor-pointer items-center justify-center gap-3 whitespace-nowrap rounded border border-grey-100 bg-white px-3 py-2 text-base leading-[1.6] text-ink [font:inherit] [&_img]:block [&_img]:size-6",
    "[&_select]:cursor-pointer [&_select]:appearance-none [&_select]:border-0 [&_select]:bg-transparent [&_select]:pr-[18px] [&_select]:text-inherit [&_select]:[font:inherit]",
    "[&_select]:bg-no-repeat [&_select]:[background-image:linear-gradient(45deg,transparent_50%,currentColor_50%),linear-gradient(135deg,currentColor_50%,transparent_50%)] [&_select]:[background-position:right_6px_center,right_1px_center] [&_select]:[background-size:5px_5px,5px_5px]",
    "[&_select]:focus-visible:rounded-sm [&_select]:focus-visible:outline-2 [&_select]:focus-visible:outline-offset-2 [&_select]:focus-visible:outline-primary",
    focus,
  ),
  btnPrimary: cx(
    "sc-btn-primary h-11 border-primary-hover bg-[linear-gradient(180deg,var(--color-primary),var(--color-primary-hover))] text-white [&_img]:size-[18px]",
  ),
  btnCompact: "h-10 gap-2 px-3 py-1.5 text-sm [&_img]:size-5",

  surface: cx(
    "sc-surface flex flex-1 flex-col gap-5 rounded-lg bg-white p-5 shadow-xl max-[900px]:p-3",
  ),
  metrics: "sc-metrics grid grid-cols-4 items-stretch gap-5",
  card: "sc-card flex flex-col gap-5 overflow-clip rounded-lg border border-grey-100 bg-white p-4",
  cardHead: "flex items-center gap-3",
  cardIcon:
    "inline-flex size-10 shrink-0 items-center justify-center rounded-sm bg-grey-25 p-2 [&_img]:block [&_img]:size-6",
  cardTitle: cx(
    "flex min-w-0 flex-col",
    "[&_strong]:font-display [&_strong]:text-lg [&_strong]:font-semibold [&_strong]:leading-[1.4] [&_strong]:tracking-[-0.02em] [&_strong]:text-ink",
    "[&_span]:text-xs [&_span]:leading-[1.55] [&_span]:text-ink-muted",
  ),
  cardValue: cx(
    "mt-auto flex items-end gap-2",
    "[&_b]:font-mono [&_b]:text-2xl [&_b]:leading-none [&_b]:font-semibold [&_b]:tracking-[-0.02em] [&_b]:text-ink",
    "[&_span]:text-sm [&_span]:leading-[1.55] [&_span]:text-ink-muted",
  ),
  pill: "mt-auto flex min-h-10 flex-1 items-center justify-center rounded text-center text-lg font-semibold leading-[1.4] tracking-[-0.02em] bg-success-wash text-success-ink",
  skeleton: "inline-block h-6 w-[2.5ch] rounded-sm bg-grey-50",
  placeholder:
    "flex min-h-60 flex-1 items-center justify-center rounded-lg border border-dashed border-grey-100 p-6 text-center text-sm text-ink-muted",
  empty: "sc-empty rounded-lg border border-dashed border-grey-100 p-4 text-sm text-ink-muted",
  error: "m-0 rounded border border-danger bg-danger-wash p-3 text-sm text-danger",
  panel: "sc-panel flex flex-col gap-5 rounded-lg border border-grey-100 bg-white p-5",
  panelHead: cx(
    "sc-panel-head flex items-start justify-between gap-4",
    "[&_h2]:m-0 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:leading-[1.4] [&_h2]:tracking-[-0.02em] [&_h2]:text-ink",
    "[&_p]:mt-1 [&_p]:mb-0 [&_p]:text-sm [&_p]:text-ink-muted",
  ),
  table: cx(
    "w-full border-collapse text-sm",
    "[&_thead_th]:bg-grey-25 [&_thead_th]:px-3 [&_thead_th]:py-3 [&_thead_th]:text-left [&_thead_th]:font-bold [&_thead_th]:whitespace-nowrap [&_thead_th]:text-ink",
    "[&_thead_th:first-child]:rounded-l [&_thead_th:last-child]:rounded-r",
    "[&_td]:border-b [&_td]:border-grey-50 [&_td]:px-3 [&_td]:py-3 [&_td]:align-middle [&_td]:text-ink",
    "[&_tbody_tr.sc-row-open]:cursor-pointer [&_tbody_tr.sc-row-open:hover_td]:bg-grey-0",
  ),
  num: "font-mono tabular-nums",
  openLink: cx(
    "sc-open-link inline-flex cursor-pointer items-center gap-2 rounded-sm border-0 bg-primary-wash px-3 py-1.5 text-sm text-primary [font:inherit] [&_img]:block [&_img]:size-3.5",
  ),
  stack: "flex min-w-0 flex-1 flex-col gap-5",
  viewerFrame:
    "sc-viewer-frame relative min-h-80 overflow-clip rounded-lg bg-grey-25",
  back: cx(
    "inline-flex size-10 shrink-0 cursor-pointer items-center justify-center rounded border border-grey-100 bg-white [&_img]:block [&_img]:size-5 [&_img]:-rotate-[135deg]",
    focus,
  ),
  pageHead: "flex h-10 items-center gap-3 [&_h1]:m-0 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:leading-[1.4] [&_h1]:tracking-[-0.02em] [&_h1]:text-ds-teal",
  erpGrid:
    "grid grid-cols-[minmax(0,0.92fr)_minmax(0,1.18fr)_minmax(0,0.9fr)] items-start gap-5",
  erpCol: "flex min-w-0 flex-col gap-5",
  erpCard: cx(
    "flex min-w-0 flex-col gap-4 rounded-lg border border-grey-100 bg-white p-4",
    "[&>header]:flex [&>header]:flex-wrap [&>header]:items-start [&>header]:justify-between [&>header]:gap-3",
    "[&_h2]:m-0 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:leading-[1.4] [&_h2]:tracking-[-0.02em] [&_h2]:text-ink",
    "[&_header_p]:mt-1 [&_header_p]:mb-0 [&_header_p]:text-xs [&_header_p]:text-ink-muted",
  ),
  badge: cx(
    "sc-badge inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-2 py-1 text-xs",
    "data-[tone=ok]:bg-success-wash data-[tone=ok]:text-success-ink",
    "data-[tone=warn]:bg-warning-wash data-[tone=warn]:text-warning-ink",
  ),
  tag: cx(
    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-grey-100 bg-white px-2 py-1 text-xs font-medium text-primary",
    "[&_i]:size-2 [&_i]:shrink-0 [&_i]:rounded-full [&_i]:bg-current",
    "data-[tone=ok]:text-success-ink data-[tone=warn]:text-warning-ink data-[tone=muted]:text-ink-muted",
    "data-[tone=danger]:text-danger data-[tone=primary]:text-primary",
  ),
  headControls: "sc-head-controls flex min-w-0 flex-wrap items-center gap-3",
  cols: "sc-cols flex min-h-0 min-w-0 flex-1 items-stretch gap-5 overflow-hidden max-[1100px]:flex-col max-[1100px]:items-stretch",
  scrollPane: "min-h-0 min-w-0 flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",

  group: "flex flex-col gap-5 [&_h2]:m-0 [&_h2]:text-lg [&_h2]:font-medium [&_h2]:leading-[1.55] [&_h2]:text-ink",
  files: cx(
    "grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-4",
    "data-[layout=list]:grid-cols-1",
    "data-[layout=list]:[&_.sc-file]:flex-row data-[layout=list]:[&_.sc-file]:items-center data-[layout=list]:[&_.sc-file]:gap-4",
    "data-[layout=list]:[&_.sc-file-top]:shrink-0",
    "data-[layout=list]:[&_.sc-file-text]:min-w-0 data-[layout=list]:[&_.sc-file-text]:flex-1",
  ),
  file: cx(
    "sc-file relative flex cursor-pointer flex-col justify-center gap-5 overflow-clip rounded-lg border-0 bg-grey-25 p-3 text-left [font:inherit] hover:bg-grey-50",
    "has-[.sc-file-open:focus-visible]:outline-2 has-[.sc-file-open:focus-visible]:outline-offset-2 has-[.sc-file-open:focus-visible]:outline-primary",
  ),
  fileTop: "sc-file-top flex items-start justify-between",
  fileThumb: cx(
    "inline-flex h-[68px] w-16 items-center justify-center rounded bg-white p-1",
    "[&>span]:inline-flex [&>span]:size-12 [&>span]:items-center [&>span]:justify-center [&>span]:overflow-clip [&>span]:rounded [&>span]:bg-grey-25",
    "[&_img]:block [&_img]:size-6",
    "[&_img[data-thumb=true]]:size-full [&_img[data-thumb=true]]:object-cover",
  ),
  fileDownload: cx(
    "relative z-[2] inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-grey-100 bg-white [&_img]:block [&_img]:size-[18px]",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
  ),
  fileActions: "relative z-[2] flex shrink-0 items-start gap-1",
  fileParsing: cx(
    "pointer-events-none after:absolute after:inset-0 after:bg-[rgb(248_250_249/0.72)] after:content-['']",
    "[&_.sc-file-top]:blur-[2px] [&_.sc-file-text_strong]:blur-[1px]",
  ),
  fileParsingLabel:
    "absolute inset-0 z-[3] flex flex-col items-center justify-center gap-2 text-sm font-medium text-ink",
  fileText: cx(
    "sc-file-text flex min-w-0 flex-col justify-center text-sm leading-[1.55]",
    "[&_strong]:overflow-hidden [&_strong]:text-ellipsis [&_strong]:whitespace-nowrap [&_strong]:font-medium [&_strong]:text-ink",
    "[&_span]:overflow-hidden [&_span]:text-ellipsis [&_span]:whitespace-nowrap [&_span]:text-ink-muted",
  ),
  fileOpen:
    "sc-file-open absolute inset-0 z-[1] cursor-pointer border-0 bg-transparent p-0 disabled:cursor-default",
  toolbar: "flex min-w-0 flex-wrap items-center justify-between gap-3",
  toolbarGroup: "flex min-w-0 flex-wrap items-center gap-3",
  toolbarSearch: "min-w-0 flex-[1_1_20rem]",
  field: cx(
    "flex h-12 max-w-[22rem] min-w-[8rem] flex-[1_1_12rem] items-center gap-2 rounded-[10px] border border-grey-100 bg-white px-3",
    "[&_img]:block [&_img]:size-5",
    "[&_input]:min-w-0 [&_input]:flex-1 [&_input]:border-0 [&_input]:bg-transparent [&_input]:text-sm [&_input]:text-ink [&_input]:outline-none [&_input]:[font:inherit]",
  ),
  viewToggle: cx(
    "inline-flex h-12 items-center gap-1.5 rounded border border-grey-100 bg-white p-1",
    "[&_button]:inline-flex [&_button]:size-[38px] [&_button]:cursor-pointer [&_button]:items-center [&_button]:justify-center [&_button]:rounded-sm [&_button]:border [&_button]:border-transparent [&_button]:bg-transparent",
    "[&_button[aria-pressed=true]]:border-grey-100 [&_button[aria-pressed=true]]:bg-primary-wash",
    "[&_img]:block [&_img]:size-6",
  ),
  cellLink:
    "cursor-pointer border-0 bg-transparent p-0 text-left [font:inherit] text-ink hover:text-primary hover:underline hover:underline-offset-[3px]",
  cellMuted: "text-ink-muted",

  tiles: "grid grid-cols-4 gap-5",
  tile: cx(
    "flex cursor-pointer flex-col gap-5 overflow-clip rounded-lg border border-grey-100 p-3 text-left [font:inherit]",
    "bg-[linear-gradient(180deg,var(--color-primary-wash),var(--color-grey-0))]",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
  ),
  tileTop: "flex items-center justify-between",
  tileIcon: "inline-flex size-12 items-center justify-center rounded bg-white [&_img]:block [&_img]:size-6",
  tileOpen:
    "inline-flex size-12 items-center justify-center rounded-lg border border-grey-100 bg-white [&_img]:block [&_img]:size-[18px]",
  tileFoot: cx(
    "flex items-end justify-between gap-5",
    "[&_span]:text-sm [&_span]:font-medium [&_span]:leading-[1.55] [&_span]:text-ink",
    "[&_b]:text-lg [&_b]:font-medium [&_b]:leading-[1.55] [&_b]:text-primary",
  ),
  tree: "flex flex-col gap-2",
  treeRow: cx(
    "flex w-full cursor-pointer items-center gap-3 rounded border border-grey-100 bg-white p-3 text-left [font:inherit]",
    "hover:enabled:bg-grey-0 disabled:cursor-default data-[assembly=true]:bg-grey-25",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
  ),
  treeIcon:
    "inline-flex size-8 shrink-0 items-center justify-center rounded-sm bg-primary-wash [&_img]:block [&_img]:size-[18px]",
  treeText: cx(
    "flex min-w-0 flex-col",
    "[&_strong]:overflow-hidden [&_strong]:text-ellipsis [&_strong]:whitespace-nowrap [&_strong]:text-sm [&_strong]:font-medium [&_strong]:text-ink",
    "[&_span]:text-xs [&_span]:text-ink-muted",
  ),

  segment: cx(
    "inline-flex items-center gap-3 self-start rounded bg-grey-25 p-2",
    "[&_button]:h-8 [&_button]:cursor-pointer [&_button]:rounded-sm [&_button]:border-0 [&_button]:bg-transparent [&_button]:px-2 [&_button]:text-sm [&_button]:text-ink [&_button]:[font:inherit]",
    "[&_button[aria-pressed=true]]:bg-primary [&_button[aria-pressed=true]]:text-white",
  ),
  identity: cx(
    "sc-identity flex items-start gap-5",
    "[&_dl]:m-0 [&_dl]:grid [&_dl]:min-w-0 [&_dl]:flex-1 [&_dl]:grid-cols-[120px_minmax(0,1fr)] [&_dl]:gap-x-4 [&_dl]:gap-y-1.5 [&_dl]:text-sm",
    "[&_dt]:text-ink-muted [&_dd]:m-0 [&_dd]:text-ink [&_dd]:[overflow-wrap:anywhere]",
  ),
  partCard: "[&_.sc-identity]:px-3",
  line: cx(
    "flex flex-col gap-5 overflow-clip rounded-lg border border-grey-100 bg-grey-25 pb-4",
    "[&>header]:flex [&>header]:items-center [&>header]:justify-between [&>header]:gap-3 [&>header]:bg-[linear-gradient(180deg,var(--color-primary-bright),var(--color-primary-hover))] [&>header]:p-3",
    "[&>header_strong]:min-w-0 [&>header_strong]:overflow-hidden [&>header_strong]:text-ellipsis [&>header_strong]:whitespace-nowrap [&>header_strong]:p-2 [&>header_strong]:text-sm [&>header_strong]:font-medium [&>header_strong]:leading-[1.55] [&>header_strong]:text-white",
  ),
  lineStatus: cx(
    "inline-flex shrink-0 items-center gap-3 whitespace-nowrap rounded-lg border border-grey-100 bg-white px-3 py-1 text-xs font-medium text-ink",
    "[&_i]:size-2.5 [&_i]:shrink-0 [&_i]:rounded-full",
    "data-[tone=decided]:[&_i]:bg-success data-[tone=review]:[&_i]:bg-warning data-[tone=buy]:[&_i]:bg-primary-bright",
  ),
  kanban: "grid grid-cols-3 items-start gap-5",
  kanbanFlow: "grid-cols-[repeat(auto-fit,minmax(280px,1fr))]",
  kanbanCol: cx(
    "flex min-h-60 min-w-0 flex-col gap-3 rounded-lg border border-grey-100 bg-white p-4",
    "[&>header]:flex [&>header]:items-center [&>header]:justify-between [&>header]:gap-3",
    "[&_h2]:m-0 [&_h2]:flex [&_h2]:items-center [&_h2]:gap-2 [&_h2]:text-lg [&_h2]:font-medium [&_h2]:text-ink",
    "[&_h2_i]:size-3 [&_h2_i]:rounded-full [&_h2_i]:bg-grey-100",
    "[&_h2_i[data-tone=ok]]:bg-success [&_h2_i[data-tone=danger]]:bg-danger [&_h2_i[data-tone=warn]]:bg-warning",
    "[&>header_b]:inline-flex [&>header_b]:h-10 [&>header_b]:min-w-10 [&>header_b]:items-center [&>header_b]:justify-center [&>header_b]:rounded [&>header_b]:bg-primary [&>header_b]:px-2 [&>header_b]:text-base [&>header_b]:font-medium [&>header_b]:text-white",
  ),
  kanbanCard: cx(
    "flex flex-col gap-2 rounded border-l-[3px] border-l-grey-100 bg-grey-25 p-3",
    "data-[tone=ok]:border-l-success data-[tone=danger]:border-l-danger data-[tone=warn]:border-l-warning",
    "[&_header_strong]:block [&_header_strong]:text-sm [&_header_strong]:font-medium [&_header_strong]:text-ink",
    "[&_header_span]:text-xs [&_header_span]:text-ink-muted",
    "[&_p]:m-0 [&_p]:rounded-sm [&_p]:border [&_p]:border-grey-100 [&_p]:bg-white [&_p]:p-2 [&_p]:text-xs [&_p]:leading-normal [&_p]:text-ink",
    "[&_footer]:text-xs [&_footer]:text-ink-muted",
  ),
  formGroup: cx(
    "grid grid-cols-[220px_minmax(0,1fr)] gap-5 rounded bg-grey-25 p-4",
    "[&_h3]:m-0 [&_h3]:text-base [&_h3]:font-medium [&_h3]:text-ink",
    "[&>.form-grid:only-child]:col-span-full",
  ),
  formGrid: "form-grid grid grid-cols-2 gap-x-4 gap-y-3",
  formField: "flex flex-col gap-1.5 text-sm text-ink [&_input]:h-11 [&_input]:w-full [&_input]:flex-none",
  formActions: "flex flex-wrap items-center justify-between gap-3",
  input: cx(
    "h-10 w-24 rounded border border-grey-100 bg-white px-3 text-sm text-ink [font:inherit]",
    "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
  ),
  inputWide: "w-full",
  note: "m-0 text-xs text-ink-muted",

  filters: "flex flex-wrap items-center gap-3",
  filtersSpacer: "flex-1",
  range: cx(
    "inline-flex h-12 items-center gap-2 whitespace-nowrap rounded border border-grey-100 bg-white px-3 text-base text-ink",
    "[&_button]:inline-flex [&_button]:size-6 [&_button]:cursor-pointer [&_button]:items-center [&_button]:justify-center [&_button]:rounded-sm [&_button]:border-0 [&_button]:bg-transparent [&_button]:text-inherit [&_button]:[font:inherit]",
    "[&_button:hover:not(:disabled)]:bg-grey-25 [&_button:disabled]:cursor-default [&_button:disabled]:opacity-35",
  ),

  chart: "flex items-stretch gap-5",
  metricPicker: cx(
    "m-0 flex w-[132px] shrink-0 flex-col gap-3 rounded-lg border border-grey-100 bg-white p-3",
    "[&_legend]:p-0 [&_legend]:text-base [&_legend]:font-medium [&_legend]:tracking-[-0.02em] [&_legend]:text-ink",
    "[&_label]:flex [&_label]:cursor-pointer [&_label]:items-center [&_label]:gap-2 [&_label]:text-sm [&_label]:leading-[1.55] [&_label]:text-ink",
    "[&_label[data-disabled=true]]:cursor-default [&_label[data-disabled=true]]:text-ink-muted",
    "[&_em]:text-xs [&_em]:not-italic [&_em]:text-ink-muted",
    "[&_input]:size-4 [&_input]:cursor-inherit [&_input]:accent-primary",
  ),
  swatch: "inline-block size-2 shrink-0 rounded-full",
  chartBody: "flex min-w-0 flex-1 flex-col gap-4 rounded-lg border border-grey-100 bg-white p-4",
  chartHead: cx(
    "flex items-start justify-between gap-3",
    "[&_h3]:m-0 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-[1.4] [&_h3]:tracking-[-0.02em] [&_h3]:text-ink",
    "[&_p]:mt-1 [&_p]:mb-0 [&_p]:text-sm [&_p]:text-ink-muted",
  ),
  plots: "relative flex flex-col gap-4",
  plot: "sc-plot grid grid-cols-[110px_88px_minmax(0,1fr)] items-stretch gap-3",
  plotLabel: "flex items-center gap-2 text-sm text-ink",
  plotAxis: "flex flex-col items-end justify-between whitespace-nowrap text-xs text-ink-muted",
  plotCanvas: "relative h-16 [&_svg]:block [&_svg]:h-full [&_svg]:w-full [&_svg]:overflow-visible",
  plotBase: "stroke-grey-100 stroke-1",
  crosshair: "pointer-events-none absolute top-0 bottom-0 w-0 border-l border-dashed border-grey-500",
  xaxis: cx(
    "ml-[calc(110px+88px+24px)] flex justify-between text-xs text-ink-muted",
    "[&_span[data-on=true]]:font-medium [&_span[data-on=true]]:text-ink",
  ),
  tooltip: cx(
    "pointer-events-none absolute top-0 z-[2] flex min-w-[172px] translate-x-3 flex-col gap-1 rounded border border-grey-100 bg-white p-3 text-xs shadow-xl",
    "data-[side=left]:-translate-x-[calc(100%+12px)]",
    "[&_strong]:text-sm [&_strong]:font-semibold [&_strong]:text-ink",
    "[&_span]:flex [&_span]:items-center [&_span]:gap-2 [&_span]:text-ink-muted",
    "[&_i]:size-2 [&_i]:shrink-0 [&_i]:rounded-full",
    "[&_b]:ml-auto [&_b]:font-medium [&_b]:text-ink",
  ),
  /** Portaled next to the icon rail; `z` clears main surfaces and sticky topbar. */
  navTooltipPanel: cx(
    "pointer-events-none fixed z-[100] -translate-y-1/2 whitespace-nowrap rounded-lg border border-grey-100 bg-white px-3 py-2 text-sm font-medium text-ink shadow-xl",
    "transition-opacity duration-150 ease-out motion-reduce:transition-none",
  ),
  tableView: cx(
    "[&_summary]:cursor-pointer [&_summary]:text-sm [&_summary]:text-ink-muted",
    "[&_table]:mt-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
    "[&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-grey-50 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-right",
    "[&_td]:whitespace-nowrap [&_td]:border-b [&_td]:border-grey-50 [&_td]:px-2 [&_td]:py-1.5 [&_td]:text-right",
    "[&_th[scope=row]]:text-left [&_thead_th:first-child]:text-left",
  ),

  drawer: cx(
    "sc-drawer absolute top-0 right-0 bottom-0 z-[15] flex w-[min(440px,100%)] flex-col gap-4 overflow-y-auto rounded-[6px] border border-[#e4e4e7] bg-white p-4 shadow-xl",
    "[&_.sc-segment_button]:h-8",
  ),
  drawerHead: cx(
    "flex items-start justify-between gap-3",
    "[&_h2]:m-0 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:leading-[1.4] [&_h2]:tracking-[-0.02em] [&_h2]:text-ink [&_h2]:[overflow-wrap:anywhere]",
  ),
  drawerKicker: "m-0 mb-0.5 text-xs text-ink-muted",
  drawerSub: "mt-1 mb-0 text-sm text-ink-muted",
  drawerSection: "flex flex-col gap-2 [&_h3]:m-0 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:text-ink",
  drawerRow: "flex items-center gap-2 [&_.sc-btn]:h-10 [&_.sc-btn]:text-sm",
  route: cx(
    "m-0 flex list-none flex-col gap-1 p-0",
    "[&_li]:flex [&_li]:justify-between [&_li]:gap-3 [&_li]:rounded-sm [&_li]:bg-grey-25 [&_li]:px-2 [&_li]:py-1.5 [&_li]:text-xs",
    "[&_em]:not-italic [&_em]:text-ink-muted",
  ),
  drawerFacts: cx(
    "m-0 grid grid-cols-[80px_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm",
    "[&_dt]:text-ink-muted [&_dd]:m-0 [&_dd]:text-ink",
  ),
  drawerFoot: "mt-auto flex justify-end",

  journey: "sc-journey",
  journeyOl: "m-0 flex list-none gap-2 overflow-x-auto p-0 pb-1",
  journeyLi: "min-w-[132px] flex-[1_1_0] has-[[data-next=true]]:flex-[1.6_1_0]",
  journeyBtn: cx(
    "group/j relative flex min-h-14 w-full cursor-pointer items-start gap-2 rounded border border-grey-100 bg-white px-3 py-2 text-left text-ink [font:inherit] hover:border-primary",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
    "aria-[current=step]:border-primary aria-[current=step]:bg-primary-wash",
    "data-[next=true]:border-primary data-[next=true]:shadow-[inset_3px_0_0_var(--color-primary)]",
    "data-[next=true]:data-[state=blocked]:border-warning data-[next=true]:data-[state=blocked]:shadow-[inset_3px_0_0_var(--color-warning)]",
  ),
  journeyMark: cx(
    "inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-grey-100 text-[11px] text-ink-muted [&_i]:hidden [&_svg]:hidden",
    "group-data-[state=done]/j:border-success group-data-[state=done]/j:bg-success-wash group-data-[state=done]/j:text-success-ink group-data-[state=done]/j:[&_b]:hidden group-data-[state=done]/j:[&_svg]:block",
    "group-data-[state=current]/j:border-primary group-data-[state=current]/j:bg-primary group-data-[state=current]/j:text-white",
    "group-data-[state=blocked]/j:border-warning group-data-[state=blocked]/j:bg-warning-wash group-data-[state=blocked]/j:text-warning-ink group-data-[state=blocked]/j:[&_b]:hidden group-data-[state=blocked]/j:[&_svg]:block",
  ),
  journeyText: cx(
    "flex min-w-0 flex-col gap-0.5",
    "[&_strong]:text-[13px] [&_strong]:font-semibold",
    "[&_span]:overflow-hidden [&_span]:text-ellipsis [&_span]:whitespace-nowrap [&_span]:text-xs [&_span]:text-ink-muted",
    "group-data-[state=blocked]/j:[&_span]:text-warning-ink",
  ),
  journeyNext: cx(
    "ml-auto shrink-0 self-center rounded-[10px] bg-primary-wash px-[7px] py-0.5 text-[10px] font-semibold not-italic uppercase tracking-[0.04em] text-primary",
    "group-data-[state=blocked]/j:bg-warning-wash group-data-[state=blocked]/j:text-warning-ink",
  ),

  // Same page edges as settingsPageClass, so a workspace card lines up with the list cards.
  ws: cx(
    "sc-ws -mx-5 -mb-5 flex min-h-0 min-w-0 flex-1 flex-row gap-4 overflow-hidden px-ds-page-x py-ds-page-y",
    "max-[900px]:-mx-3 max-[900px]:-mb-3 max-[900px]:px-3 max-[900px]:py-3",
  ),
  wsViewer: cx(
    "flex min-h-0 min-w-[220px] max-w-[640px] flex-[0_0_38%] flex-col overflow-hidden rounded-lg border border-grey-100 bg-white shadow-xl",
    "transition-[flex-basis,min-width,max-width,opacity,border-color] duration-200 ease-out motion-reduce:transition-none",
    "max-[900px]:min-w-[160px] max-[900px]:flex-[0_0_36%] max-[900px]:max-w-none",
    "data-[open=false]:pointer-events-none data-[open=false]:min-w-0 data-[open=false]:max-w-0 data-[open=false]:flex-[0_0_0%] data-[open=false]:border-transparent data-[open=false]:opacity-0",
  ),
  wsViewerHead: "flex items-center justify-between gap-2 border-b border-grey-100 py-2 pr-2 pl-4",
  wsViewerTitle: "flex min-w-0 items-center gap-2 text-sm",
  wsStatus: cx(
    "rounded-[10px] bg-grey-25 px-2 py-0.5 text-[11px] font-medium text-ink-muted capitalize",
    "data-[status=ready]:bg-success-wash data-[status=ready]:text-success-ink",
    "data-[status=done]:bg-success-wash data-[status=done]:text-success-ink",
    "data-[status=completed]:bg-success-wash data-[status=completed]:text-success-ink",
    "data-[status=failed]:bg-danger-wash data-[status=failed]:text-danger",
    "data-[status=error]:bg-danger-wash data-[status=error]:text-danger",
  ),
  wsIconBtn: cx(
    "inline-flex size-9 cursor-pointer items-center justify-center rounded border border-transparent bg-transparent text-ink-muted",
    "hover:border-grey-100 hover:text-ink",
    focus,
  ),
  wsViewerBody: "min-h-0 flex-1 [&_.sc-viewer-frame]:h-full [&_.sc-viewer-frame]:min-h-0 [&_.sc-viewer-frame]:rounded-none",
  wsViewerFoot: cx(
    "flex items-center gap-1 border-t border-grey-100 py-2 pr-2 pl-4 text-[13px] text-ink-muted",
    "[&_strong]:text-ink",
  ),
  wsRail: cx(
    "flex w-12 shrink-0 cursor-pointer flex-col items-center gap-2 rounded-lg border border-grey-100 bg-white py-3 text-xs text-primary [font:inherit] hover:bg-primary-wash",
    "[&_span]:[writing-mode:vertical-rl] [&_span]:rotate-180",
    focus,
  ),
  wsContent: cx(
    "sc-ws-content relative flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden bg-transparent p-0 font-ds text-ds-ink",
    "[&>.sc-drawer]:!top-0 [&>.sc-drawer]:!right-0 [&>.sc-drawer]:!bottom-0 [&>.sc-drawer]:!h-auto [&>.sc-drawer]:!rounded-[6px]",
  ),
  wsHead: "flex flex-wrap items-center justify-between gap-3 border-b border-grey-100 px-5 pt-4 pb-3",
  wsHeadActions: "ml-auto flex flex-wrap items-center justify-end gap-2",
  wsBody:
    "sc-ws-body flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-auto px-5 pt-4 pb-5 has-[>[data-fit=true]]:overflow-hidden",
  wsEmbedded: "flex min-w-0 flex-col gap-4 data-[fit=true]:h-full data-[fit=true]:min-h-0 data-[fit=true]:flex-1 data-[fit=true]:overflow-hidden",
  wsSummary: "m-0 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-ink-muted [&_b]:font-semibold [&_b]:text-ink",
  wsQuote: "ml-auto [&_.sc-btn]:h-10 [&_.sc-btn]:text-sm",
  costMetrics: "sc-cost-metrics grid-cols-[repeat(auto-fit,minmax(150px,1fr))]",

  gauge: "flex flex-col items-center gap-2 [&_svg]:block [&_svg]:h-auto [&_svg]:w-full [&_svg]:max-w-[240px]",
  gaugeTrack: "fill-none stroke-grey-50 stroke-[16]",
  gaugeFill: "fill-none stroke-primary stroke-[16]",
  gaugeTick: "fill-ink text-[13px] font-medium",
  gaugeNeedle: "[&_line]:stroke-primary [&_line]:stroke-[4] [&_line]:[stroke-linecap:round] [&_circle]:fill-primary",
  gaugeValue: "m-0 flex items-baseline gap-1.5 [&_b]:text-[32px] [&_b]:leading-none [&_b]:font-semibold [&_b]:tracking-[-0.02em] [&_b]:text-ink [&_span]:text-sm [&_span]:text-ink-muted",
  gaugeEmpty: "text-sm text-ink-muted",
  meter: "flex flex-col gap-1.5",
  meterLabel: "flex items-baseline justify-between gap-2 text-sm text-ink [&_em]:text-xs [&_em]:not-italic [&_em]:text-ink-muted",
  meterTrack: "block h-2 overflow-hidden rounded bg-grey-50",
  meterFill: "block h-full rounded bg-primary",
  heatmap: "flex flex-col gap-2",
  heatmapGrid: "flex flex-col gap-1",
  heatmapRow: "grid grid-cols-[inherit] items-center gap-1",
  heatmapRowLabel: "overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-ink-muted",
  heatmapCell: cx(
    "h-8 cursor-default rounded-sm shadow-[0_0_0_2px_#fff]",
    "data-[on=true]:shadow-[0_0_0_2px_#fff,0_0_0_3px_var(--color-grey-500)]",
  ),
  heatmapColLabel: "text-center text-[11px] text-ink-muted",
  heatmapRead: "m-0 text-xs text-ink-muted",
  heatmapCallout: cx(
    "m-0 flex w-full min-w-0 items-baseline justify-center gap-2 rounded-lg border border-grey-100 bg-white px-2.5 py-[7px] text-center text-sm shadow-[0_8px_8px_rgba(16,24,40,0.03)]",
    "[&_i]:size-2 [&_i]:shrink-0 [&_i]:-translate-y-px [&_i]:rounded-full [&_i]:bg-primary-bright",
    "[&_strong]:shrink-0 [&_strong]:font-medium [&_strong]:text-ink [&_span]:min-w-0 [&_span]:truncate [&_span]:text-ink-muted",
  ),
  attention: "flex flex-col gap-3",
  attentionItem: cx(
    "flex flex-col gap-1.5 rounded bg-grey-25 p-3",
    "[&_strong]:flex [&_strong]:items-center [&_strong]:gap-2 [&_strong]:text-sm [&_strong]:font-medium [&_strong]:text-ink",
    "[&_strong]:before:inline-flex [&_strong]:before:size-4 [&_strong]:before:shrink-0 [&_strong]:before:items-center [&_strong]:before:justify-center [&_strong]:before:rounded-full [&_strong]:before:bg-warning-wash [&_strong]:before:text-[11px] [&_strong]:before:font-bold [&_strong]:before:text-warning-ink [&_strong]:before:content-['!']",
    "[&_p]:m-0 [&_p]:text-xs [&_p]:leading-normal [&_p]:text-ink-muted",
    "[&_footer]:text-[11px] [&_footer]:text-ink-muted",
  ),
  attentionRows: "flex gap-4 text-xs text-ink [&_b]:font-medium",
  readiness: "flex flex-col gap-4",
  readinessFigure: cx(
    "m-0 flex items-baseline gap-2",
    "[&_b]:font-mono [&_b]:text-[40px] [&_b]:leading-none [&_b]:font-semibold [&_b]:tracking-[-0.02em] [&_b]:text-ink",
    "[&_span]:text-sm [&_span]:text-ink-muted",
  ),
  nextstep: cx(
    "flex flex-col gap-2 rounded bg-primary-wash p-3",
    "[&_strong]:text-sm [&_strong]:font-medium [&_strong]:text-ink",
    "[&_p]:m-0 [&_p]:text-xs [&_p]:leading-normal [&_p]:text-ink-muted",
    "[&_.sc-btn]:h-10 [&_.sc-btn]:self-start [&_.sc-btn]:text-sm",
  ),
  stockValue: cx(
    "[&_dl]:m-0 [&_dl]:grid [&_dl]:grid-cols-[auto_1fr] [&_dl]:gap-x-3 [&_dl]:gap-y-1.5 [&_dl]:text-sm",
    "[&_dt]:text-ink-muted [&_dd]:m-0 [&_dd]:font-mono [&_dd]:text-ink [&_dd]:tabular-nums",
  ),
  chips: cx(
    "inline-flex items-center gap-2 self-start rounded bg-grey-25 p-2",
    "[&_button]:h-8 [&_button]:cursor-pointer [&_button]:whitespace-nowrap [&_button]:rounded-sm [&_button]:border-0 [&_button]:bg-transparent [&_button]:px-3 [&_button]:text-sm [&_button]:text-ink [&_button]:[font:inherit]",
    "[&_button[aria-pressed=true]]:bg-primary [&_button[aria-pressed=true]]:text-white",
  ),
  sectionHead: cx(
    "flex items-center justify-between gap-3",
    "[&_h2]:m-0 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:leading-[1.4] [&_h2]:tracking-[-0.02em] [&_h2]:text-ink",
  ),
  sectionBlurb: "mt-1 mb-0 text-xs text-ink-muted",
  rowActions: cx(
    "inline-flex items-center gap-2 rounded-sm bg-primary-wash px-2 py-1",
    "[&_button]:cursor-pointer [&_button]:border-0 [&_button]:bg-transparent [&_button]:p-0 [&_button]:text-sm [&_button]:text-primary [&_button]:[font:inherit]",
    "[&_button:disabled]:cursor-default [&_button:disabled]:text-ink-muted",
    "[&_span]:text-primary",
  ),
  bars: "sc-bars flex flex-col gap-3",
  barsPlot: "sc-bars-plot flex gap-3",
  barsAxis:
    "sc-bars-axis flex h-[200px] flex-col items-end justify-between whitespace-nowrap pb-[22px] text-[11px] text-ink-muted",
  barsGrid:
    "sc-bars-grid relative flex h-[200px] min-w-0 flex-1 items-end gap-2 border-b border-grey-100 pb-[22px]",
  barsRef: "pointer-events-none absolute right-0 left-0 mb-[22px] border-t border-dashed border-grey-500",
  barsGroup: "relative flex h-full min-w-0 flex-[1_1_0] flex-col items-center justify-end gap-1.5 data-[on=true]:bg-grey-0",
  barsStack: "flex h-full w-full items-end justify-center gap-0.5",
  bar: "max-w-[22px] min-h-px w-[40%] rounded-t",
  barsLabel: "absolute bottom-[-22px] max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-ink-muted",
  barsLegend: cx(
    "flex flex-wrap gap-4 text-xs text-ink-muted",
    "[&_span]:inline-flex [&_span]:items-center [&_span]:gap-1.5",
    "[&_i]:size-2 [&_i]:rounded-full",
  ),
  hero: cx(
    "m-0 flex items-baseline gap-3",
    "[&_b]:text-5xl [&_b]:leading-none [&_b]:font-semibold [&_b]:tracking-[-0.02em] [&_b]:text-ink",
    "[&_span]:text-sm [&_span]:text-ink-muted",
  ),
  opList: "flex flex-col gap-3",
  op: "flex items-center gap-4 rounded-lg border border-grey-100 bg-white p-3",
  opMain: cx(
    "flex min-w-0 flex-1 flex-col gap-1",
    "[&_strong]:text-sm [&_strong]:font-medium [&_strong]:text-ink",
    "[&_span]:text-xs [&_span]:text-ink-muted",
  ),
  opFigures: cx(
    "flex gap-4 whitespace-nowrap text-xs text-ink-muted",
    "[&_b]:block [&_b]:text-sm [&_b]:font-medium [&_b]:text-ink",
  ),
  inlineForm: cx(
    "flex flex-wrap items-end gap-3 rounded-lg border border-grey-100 bg-white p-4",
    "[&_label]:flex [&_label]:flex-col [&_label]:gap-1.5 [&_label]:text-xs [&_label]:text-ink-muted",
    "[&_input]:h-10 [&_input]:min-w-40 [&_input]:rounded [&_input]:border [&_input]:border-grey-100 [&_input]:bg-white [&_input]:px-3 [&_input]:text-sm [&_input]:text-ink [&_input]:[font:inherit]",
    "[&_select]:h-10 [&_select]:min-w-40 [&_select]:rounded [&_select]:border [&_select]:border-grey-100 [&_select]:bg-white [&_select]:px-3 [&_select]:text-sm [&_select]:text-ink [&_select]:[font:inherit]",
    "[&_input:focus]:outline-2 [&_input:focus]:outline-offset-[-1px] [&_input:focus]:outline-primary",
    "[&_select:focus]:outline-2 [&_select:focus]:outline-offset-[-1px] [&_select:focus]:outline-primary",
  ),
  emptyState: cx(
    "items-center p-16 text-center",
    "[&_h2]:mt-3 [&_h2]:mb-0 [&_h2]:text-[28px] [&_h2]:font-semibold [&_h2]:tracking-[-0.02em] [&_h2]:text-ink",
    "[&>p]:m-0 [&>p]:max-w-[640px] [&>p]:text-base [&>p]:leading-normal [&>p]:text-ink-muted",
  ),
  actionCards: "mt-6 grid w-full max-w-[1120px] grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 text-left",
  actionCard: cx(
    "flex flex-col gap-2 rounded-lg border border-grey-100 p-4",
    "bg-[linear-gradient(180deg,var(--color-primary-wash),#fff_55%)]",
    "[&_h3]:m-0 [&_h3]:py-6 [&_h3]:text-center [&_h3]:text-lg [&_h3]:font-medium [&_h3]:text-primary",
    "[&_p]:m-0 [&_p]:flex-1 [&_p]:text-[13px] [&_p]:leading-normal [&_p]:text-ink-muted",
    "[&_.sc-btn]:h-10 [&_.sc-btn]:justify-between [&_.sc-btn]:text-sm",
  ),
  compose: cx(
    "flex flex-col gap-2 rounded bg-grey-25 p-3",
    "[&_h3]:m-0 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:text-ink",
  ),
  composeRow: cx(
    "flex flex-wrap items-center gap-2",
    "[&_.sc-btn]:h-10 [&_.sc-btn]:text-sm",
    "[&_input]:!w-auto [&_input]:min-w-40 [&_input]:flex-[1_1_200px]",
  ),
  twoUp: "grid grid-cols-2 gap-5 max-[1100px]:grid-cols-1",
  switchList: "flex flex-col gap-2",
  switchRow: cx(
    "flex cursor-pointer items-center justify-between gap-3 py-1.5 text-sm text-ink",
    "[&_span_em]:block [&_span_em]:text-xs [&_span_em]:not-italic [&_span_em]:text-ink-muted",
  ),
  switchRowMuted: "mt-2 border-t border-grey-100 pt-3 text-ink-muted",
  radioRow: cx(
    "flex cursor-pointer items-center justify-start gap-3 py-1.5 text-sm text-ink",
    "[&_span_em]:block [&_span_em]:text-xs [&_span_em]:not-italic [&_span_em]:text-ink-muted",
    "[&_input]:size-[22px] [&_input]:accent-primary",
  ),
  switch: cx(
    "relative h-[30px] w-[52px] shrink-0 cursor-pointer appearance-none rounded-[15px] border-0 bg-grey-100 transition-colors duration-[120ms]",
    "after:absolute after:top-[3px] after:left-[3px] after:size-6 after:rounded-full after:bg-white after:shadow-[0_1px_2px_rgba(11,18,17,0.2)] after:transition-transform after:duration-[120ms] after:content-['']",
    "checked:bg-primary checked:after:translate-x-[22px]",
    focus,
  ),
  homeCta: "flex flex-col items-start gap-3",

  srOnly: "sr-only",
  ai: "sc-ai min-h-0 flex-1 !gap-3 !p-3",
  aiBody: "relative flex min-h-0 flex-1 gap-3",
  aiMain: "flex min-h-0 min-w-0 flex-1 flex-col gap-3",
  aiHistory: cx(
    "flex min-h-0 w-[220px] shrink-0 flex-col gap-1 border-r border-grey-100 pr-2",
    "max-[900px]:w-[188px]",
  ),
  aiRailBtn: cx(
    "inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-ink-muted",
    "hover:bg-grey-25 hover:text-ink",
    "disabled:cursor-not-allowed disabled:opacity-40",
    focus,
  ),
  aiRailSearch: cx(
    "flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 text-ink-muted",
    "focus-within:bg-grey-25 focus-within:text-ink",
    "[&_input]:min-w-0 [&_input]:flex-1 [&_input]:border-0 [&_input]:bg-transparent [&_input]:text-xs [&_input]:text-ink [&_input]:outline-none [&_input]:[font:inherit]",
    "[&_input]::placeholder:text-ink-muted",
  ),
  aiHistoryNote: "m-0 text-xs text-ink-muted",
  aiHistoryList: cx(
    "m-0 flex min-h-0 flex-1 list-none flex-col gap-1 overflow-y-auto p-0",
    "[&_li]:flex [&_li]:items-stretch [&_li]:gap-1",
  ),
  aiHistoryItem: cx(
    "flex min-w-0 flex-1 cursor-pointer flex-col gap-0 rounded border border-transparent bg-transparent px-2 py-1.5 text-left text-ink [font:inherit] hover:bg-white",
    "aria-[current=true]:border-grey-100 aria-[current=true]:bg-white",
    "[&_strong]:overflow-hidden [&_strong]:text-ellipsis [&_strong]:whitespace-nowrap [&_strong]:text-[13px] [&_strong]:font-medium",
    "[&_span]:text-[11px] [&_span]:text-ink-muted",
    focus,
  ),
  aiHistoryDel: cx(
    "inline-flex w-7 shrink-0 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-ink-muted opacity-0 hover:bg-danger-wash hover:text-danger group-hover:opacity-100 focus-visible:opacity-100",
    focus,
  ),
  aiHistoryToggle: cx(
    "relative h-10 gap-2 text-sm aria-[expanded=true]:border-primary aria-[expanded=true]:bg-primary-wash",
    "max-[900px]:w-10 max-[900px]:p-0 max-[900px]:[&>span:not(.sc-ai-count)]:hidden",
    "max-[900px]:[&_.sc-ai-count]:absolute max-[900px]:[&_.sc-ai-count]:-top-1.5 max-[900px]:[&_.sc-ai-count]:-right-1.5",
  ),
  aiCount:
    "sc-ai-count inline-flex h-5 min-w-5 items-center justify-center rounded-[10px] bg-primary px-1.5 text-[11px] font-semibold text-white",
  aiThreadWrap: "relative flex min-h-0 flex-1 flex-col",
  aiThread: cx(
    "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-10 [overflow-anchor:none]",
    "max-[900px]:[&_.sc-ai-row]:max-w-[92%]",
  ),
  aiIntro: "m-auto flex max-w-[520px] flex-col items-center gap-4 text-center text-sm leading-[1.55] text-ink-muted [&_p]:m-0",
  aiSuggestions: cx(
    "flex flex-wrap justify-center gap-2",
    "[&_.sc-btn]:h-auto [&_.sc-btn]:min-h-9 [&_.sc-btn]:whitespace-normal [&_.sc-btn]:text-center [&_.sc-btn]:text-sm",
  ),
  aiRow: "sc-ai-row group flex max-w-[80%] items-start gap-2.5 data-[role=you]:flex-row-reverse data-[role=you]:self-end",
  aiAvatar: cx(
    "inline-flex size-7 shrink-0 items-center justify-center border border-grey-100 bg-white text-[11px] font-semibold text-ink",
    "[&_img]:size-3.5",
  ),
  aiTurn: cx(
    "relative max-w-none px-3 py-2 data-[role=you]:border data-[role=you]:border-[#e4e4e7] data-[role=you]:bg-[#f6f7f8] data-[role=you]:text-[#18181b] data-[role=ai]:bg-white data-[role=ai]:self-start",
    "data-[failed=true]:border data-[failed=true]:border-[#f0c4bc] data-[role=ai]:data-[failed=true]:bg-danger-wash",
    "[&_p]:m-0 [&_p]:text-[13px] [&_p]:leading-relaxed [&_p]:text-inherit [&_p]:[overflow-wrap:anywhere] [&_p]:whitespace-pre-wrap",
    "data-[role=you]:[&_p]:text-[#18181b]",
    "[&_.orch-markdown]:text-sm [&_.orch-markdown]:leading-[1.55] [&_.orch-markdown]:text-ink [&_.orch-markdown]:[overflow-wrap:anywhere]",
    "[&_.orch-markdown_.md-p]:mb-[0.6em] [&_.orch-markdown_.md-p]:whitespace-normal [&_.orch-markdown_.md-p]:text-ink [&_.orch-markdown_.md-p]:last:mb-0",
    "[&_.orch-markdown_.md-h]:mt-3 [&_.orch-markdown_.md-h]:mb-1 [&_.orch-markdown_.md-h]:text-sm [&_.orch-markdown_.md-h]:font-semibold [&_.orch-markdown_.md-h]:text-ink",
    "[&_.orch-markdown_.md-h3]:mt-3 [&_.orch-markdown_.md-h3]:mb-1 [&_.orch-markdown_.md-h3]:text-sm [&_.orch-markdown_.md-h3]:font-semibold [&_.orch-markdown_.md-h3]:text-ink",
    "[&_.orch-markdown_.md-ul]:mb-2 [&_.orch-markdown_.md-ul]:pl-5 [&_.orch-markdown_.md-ol]:mb-2 [&_.orch-markdown_.md-ol]:pl-5",
    "[&_.orch-markdown_.md-li]:my-0.5 [&_.orch-markdown_.md-li]:text-ink [&_.orch-markdown_.md-li]:marker:text-ink-muted",
    "[&_.orch-markdown_.md-strong]:font-semibold [&_.orch-markdown_.md-strong]:text-ink",
    "[&_.orch-markdown_.md-code]:rounded [&_.orch-markdown_.md-code]:border [&_.orch-markdown_.md-code]:border-grey-100 [&_.orch-markdown_.md-code]:bg-white [&_.orch-markdown_.md-code]:px-[5px] [&_.orch-markdown_.md-code]:py-px [&_.orch-markdown_.md-code]:text-[12.5px] [&_.orch-markdown_.md-code]:text-ink",
    "[&_.orch-markdown_.md-pre]:my-2 [&_.orch-markdown_.md-pre]:overflow-x-auto [&_.orch-markdown_.md-pre]:rounded [&_.orch-markdown_.md-pre]:border [&_.orch-markdown_.md-pre]:border-grey-100 [&_.orch-markdown_.md-pre]:bg-white [&_.orch-markdown_.md-pre]:px-3 [&_.orch-markdown_.md-pre]:py-2",
    "[&_.orch-markdown_.md-code-block]:text-[12.5px] [&_.orch-markdown_.md-code-block]:text-ink",
    "[&_.orch-markdown_.md-quote]:my-2 [&_.orch-markdown_.md-quote]:border-l-2 [&_.orch-markdown_.md-quote]:border-grey-100 [&_.orch-markdown_.md-quote]:pl-3 [&_.orch-markdown_.md-quote]:text-ink-muted",
    "[&_.orch-markdown_.md-link]:text-ds-teal",
    "[&_.orch-markdown_.md-table-wrap]:my-2 [&_.orch-markdown_.md-table-wrap]:overflow-x-auto",
    "[&_.orch-markdown_.md-table]:border-collapse [&_.orch-markdown_.md-table]:border-grey-100 [&_.orch-markdown_.md-table]:text-[13px]",
    "[&_.orch-markdown_.md-th]:border-b [&_.orch-markdown_.md-th]:border-grey-100 [&_.orch-markdown_.md-th]:bg-transparent [&_.orch-markdown_.md-th]:px-2.5 [&_.orch-markdown_.md-th]:py-1 [&_.orch-markdown_.md-th]:text-left [&_.orch-markdown_.md-th]:font-semibold [&_.orch-markdown_.md-th]:text-ink-muted",
    "[&_.orch-markdown_.md-td]:border-b [&_.orch-markdown_.md-td]:border-grey-100 [&_.orch-markdown_.md-td]:bg-transparent [&_.orch-markdown_.md-td]:px-2.5 [&_.orch-markdown_.md-td]:py-1 [&_.orch-markdown_.md-td]:text-left [&_.orch-markdown_.md-td]:text-ink",
    "[&_.orch-markdown_.md-thead]:bg-transparent [&_.orch-markdown_.md-tr]:bg-transparent [&_.orch-markdown_.md-tr:nth-child(even)]:bg-transparent [&_.orch-markdown_.md-tr:nth-child(even)_.md-td]:bg-transparent",
    "[&_.orch-markdown_.md-cursor]:text-[#18181b] motion-reduce:[&_.orch-markdown_.md-cursor]:animate-none",
  ),
  aiTime: "mt-1.5 block text-[11px] text-ink-muted data-[role=you]:text-white/70",
  aiCaret: "ml-0.5 inline-block h-3.5 w-0.5 animate-pulse bg-current align-text-bottom motion-reduce:animate-none",
  aiHover: cx(
    "absolute top-0 -right-8 flex flex-col gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100",
  ),
  aiHoverBtn: cx(
    "inline-flex size-6 cursor-pointer items-center justify-center border border-grey-100 bg-white text-ink-muted",
    "hover:text-ink disabled:cursor-not-allowed disabled:opacity-40",
    focus,
  ),
  aiPending: "flex min-h-[22px] items-center",
  aiDots: cx(
    "inline-flex gap-[5px] [&_i]:size-1.5 [&_i]:rounded-full [&_i]:bg-primary [&_i]:opacity-35 [&_i]:animate-ai-dot",
    "[&_i:nth-child(2)]:[animation-delay:0.15s] [&_i:nth-child(3)]:[animation-delay:0.3s]",
    "motion-reduce:[&_i]:animate-none motion-reduce:[&_i]:opacity-70",
  ),
  aiFail: "flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-danger",
  aiRetry: "h-8 px-3 text-[13px] text-ink",
  aiMeta: "mt-1.5 flex items-center justify-between gap-3 [&>span]:mt-0",
  aiNote: "mt-1.5 inline-block text-[11px] text-ink-muted",
  aiActions: "ml-auto inline-flex gap-1 [&_button]:ml-0",
  aiCopy: cx(
    "ml-auto cursor-pointer rounded-sm border-0 bg-transparent px-1.5 py-1 text-xs text-ink-muted [font:inherit] hover:bg-white hover:text-primary",
    "disabled:cursor-not-allowed disabled:opacity-50 data-[copied=true]:text-success-ink",
    focus,
  ),
  aiRefs: cx(
    "mt-3 flex flex-col gap-1.5",
    "[&_button]:flex [&_button]:cursor-pointer [&_button]:items-center [&_button]:justify-between [&_button]:gap-2 [&_button]:rounded [&_button]:border [&_button]:border-grey-100 [&_button]:bg-grey-25 [&_button]:px-3 [&_button]:py-2.5 [&_button]:text-left [&_button]:text-sm [&_button]:text-primary [&_button]:[font:inherit]",
    "[&_button_img]:size-4",
    "[&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-offset-2 [&_button:focus-visible]:outline-primary",
  ),
  aiJump: cx(
    "absolute bottom-3 left-1/2 h-9 -translate-x-1/2 gap-2 text-[13px] shadow-xl [&_img]:size-4",
    focus,
  ),
  aiComposer: cx(
    "flex w-full items-end gap-1 rounded-lg border border-grey-100 bg-white px-2 py-1.5",
    "focus-within:border-primary",
    "[&_textarea]:max-h-32 [&_textarea]:min-h-8 [&_textarea]:min-w-0 [&_textarea]:flex-1 [&_textarea]:resize-none [&_textarea]:border-0 [&_textarea]:bg-transparent [&_textarea]:px-1 [&_textarea]:py-1.5 [&_textarea]:text-sm [&_textarea]:leading-normal [&_textarea]:text-ink [&_textarea]:[font:inherit]",
    "[&_textarea]:placeholder:text-ink-muted [&_textarea]:focus:outline-none",
  ),
  aiComposerRow: "flex min-w-0 flex-1 items-end gap-1",
  aiSend: "!size-7 shrink-0 !rounded-md !p-0 disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:block",
  aiStop: "!size-7 shrink-0 !rounded-md !p-0 text-danger hover:bg-danger-wash [&_svg]:block",
  aiComposerFoot: "flex w-full justify-center",
  aiChip: cx(
    "inline-flex h-8 max-w-[260px] items-center gap-1.5 rounded-2xl border border-grey-100 bg-primary-wash py-0 pr-1 pl-2.5 text-[13px] text-ink",
    "[&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:whitespace-nowrap",
    "[&_img]:size-4",
    "[&_button]:inline-flex [&_button]:size-7 [&_button]:cursor-pointer [&_button]:items-center [&_button]:justify-center [&_button]:rounded-full [&_button]:border-0 [&_button]:bg-transparent [&_button]:text-ink-muted",
    "[&_button:hover]:bg-white [&_button:hover]:text-danger",
    "[&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-offset-2 [&_button:focus-visible]:outline-primary",
  ),
  aiHint: "ml-auto text-xs text-ink-muted max-[720px]:hidden",

  intakeSteps: cx(
    "m-0 flex list-none flex-wrap gap-x-5 gap-y-2 p-0 text-[13px] text-ink-muted",
    "[&_li]:inline-flex [&_li]:items-center [&_li]:gap-2",
    "[&_li_span]:inline-flex [&_li_span]:size-[22px] [&_li_span]:items-center [&_li_span]:justify-center [&_li_span]:rounded-full [&_li_span]:border [&_li_span]:border-grey-100 [&_li_span]:text-xs [&_li_span]:font-semibold",
    "[&_li[data-current=true]]:text-ink",
    "[&_li[data-current=true]_span]:border-ds-amber [&_li[data-current=true]_span]:bg-ds-amber [&_li[data-current=true]_span]:text-ds-on-amber",
  ),
  intakeGrid: "grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-5 max-[900px]:grid-cols-1",
  intakeMail: cx(
    "flex flex-col gap-2 text-sm font-medium",
    "[&_textarea]:min-h-[260px] [&_textarea]:w-full [&_textarea]:resize-y [&_textarea]:rounded [&_textarea]:border [&_textarea]:border-grey-100 [&_textarea]:bg-white [&_textarea]:p-3 [&_textarea]:text-sm [&_textarea]:font-normal [&_textarea]:leading-normal [&_textarea]:text-ink [&_textarea]:[font:inherit]",
    "[&_textarea:focus-visible]:outline-2 [&_textarea:focus-visible]:outline-offset-[-1px] [&_textarea:focus-visible]:outline-primary",
    "max-[900px]:[&_textarea]:min-h-40",
  ),
  intakeFiles: "flex flex-col gap-3",
  intakeDrop: cx(
    "flex min-h-[180px] flex-1 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-grey-100 bg-grey-0 p-6 text-center",
    "hover:border-ds-amber hover:bg-ds-amber-soft data-[over=true]:border-ds-amber data-[over=true]:bg-ds-amber-soft",
    "[&_strong]:text-[15px] [&_strong]:font-semibold",
    "[&_span:last-child]:text-[13px] [&_span:last-child]:text-ink-muted",
    focus,
  ),
  intakeList: cx(
    "m-0 flex list-none flex-col gap-1.5 p-0",
    "[&_li]:flex [&_li]:items-center [&_li]:gap-2 [&_li]:rounded [&_li]:border [&_li]:border-grey-100 [&_li]:bg-white [&_li]:py-1.5 [&_li]:pr-1.5 [&_li]:pl-3 [&_li]:text-sm",
    "[&_img]:size-[18px]",
    "[&_button]:inline-flex [&_button]:size-8 [&_button]:cursor-pointer [&_button]:items-center [&_button]:justify-center [&_button]:rounded-sm [&_button]:border-0 [&_button]:bg-transparent [&_button]:text-lg [&_button]:text-ink-muted",
    "[&_button:hover]:bg-danger-wash [&_button:hover]:text-danger",
  ),
  intakeName: "min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap",
  intakeKind: "text-xs text-ink-muted",
  intakeFoot: "flex flex-wrap items-center justify-end gap-3",
  intakeStatus: "mr-auto text-[13px] text-ink-muted",

  understand: "flex flex-col gap-3 [&_.sc-panel-head_p]:m-0",
  understandSteps: "self-start",
  understandCount: "ml-1.5 rounded-[9px] bg-warning px-1.5 text-[11px] text-white",
  understandCheck: "flex flex-col gap-3",
  understandQty: "!w-24 text-right",
  understandSrc: "text-[13px] text-ink-muted",
  understandActions: "flex flex-wrap justify-end gap-2 [&_.sc-btn]:h-10 [&_.sc-btn]:text-sm",
  understandAsk: "grid grid-cols-2 gap-5 max-[900px]:grid-cols-1",
  understandGaps: cx(
    "flex flex-col gap-3",
    "[&_h3]:m-0 [&_h3]:text-sm [&_h3]:font-semibold",
    "[&>.sc-btn]:h-9 [&>.sc-btn]:self-start [&>.sc-btn]:text-[13px]",
  ),
  understandFindings: cx(
    "sc-understand-findings m-0 flex list-none flex-col gap-1.5 p-0",
    "[&_li]:flex [&_li]:items-start [&_li]:gap-2 [&_li]:rounded [&_li]:border [&_li]:border-grey-100 [&_li]:bg-white [&_li]:px-3 [&_li]:py-2 [&_li]:text-sm [&_li]:leading-normal",
    "[&_.sc-badge]:mt-0.5 [&_.sc-badge]:shrink-0 [&_.sc-badge]:capitalize",
  ),
  understandMail: cx(
    "flex flex-col gap-3 rounded-lg border border-grey-100 bg-grey-0 p-4",
    "[&_h3]:m-0 [&_h3]:text-sm [&_h3]:font-semibold",
    "[&_label]:flex [&_label]:flex-col [&_label]:gap-1 [&_label]:text-xs [&_label]:font-medium [&_label]:text-ink-muted",
    "[&_textarea]:min-h-[200px] [&_textarea]:w-full [&_textarea]:resize-y [&_textarea]:rounded [&_textarea]:border [&_textarea]:border-grey-100 [&_textarea]:bg-white [&_textarea]:p-3 [&_textarea]:text-sm [&_textarea]:font-normal [&_textarea]:leading-normal [&_textarea]:text-ink [&_textarea]:[font:inherit]",
    "[&_input]:w-full [&_input]:rounded [&_input]:border [&_input]:border-grey-100 [&_input]:bg-white [&_input]:p-3 [&_input]:text-sm [&_input]:font-normal [&_input]:text-ink [&_input]:[font:inherit]",
    "[&_textarea:focus-visible]:outline-2 [&_textarea:focus-visible]:outline-offset-[-1px] [&_textarea:focus-visible]:outline-primary",
    "[&_input:focus-visible]:outline-2 [&_input:focus-visible]:outline-offset-[-1px] [&_input:focus-visible]:outline-primary",
  ),
  understandDupes: cx(
    "flex flex-wrap items-center gap-x-3 gap-y-2 rounded border border-warning bg-warning-wash px-3 py-2 text-sm",
    "[&_.sc-btn]:h-8 [&_.sc-btn]:text-[13px]",
  ),
  understandFacts: cx(
    "grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3",
    "[&_label]:flex [&_label]:flex-col [&_label]:gap-1 [&_label]:rounded [&_label]:border [&_label]:border-grey-100 [&_label]:bg-white [&_label]:px-3 [&_label]:py-2 [&_label]:text-xs [&_label]:font-medium [&_label]:text-ink-muted",
    "[&_label[data-missing=true]]:border-warning [&_label[data-missing=true]]:bg-warning-wash",
    "[&_input]:h-[34px] [&_input]:rounded-sm [&_input]:border [&_input]:border-grey-100 [&_input]:bg-white [&_input]:px-2 [&_input]:text-sm [&_input]:font-medium [&_input]:leading-8 [&_input]:text-ink [&_input]:[font:inherit]",
    "[&_output]:h-[34px] [&_output]:border-transparent [&_output]:bg-transparent [&_output]:px-0 [&_output]:text-sm [&_output]:font-medium [&_output]:leading-8 [&_output]:text-ink",
    "[&_small]:text-[11px] [&_small]:font-normal",
    "[&_input:focus-visible]:outline-2 [&_input:focus-visible]:outline-offset-[-1px] [&_input:focus-visible]:outline-primary",
  ),
  rfqTable: cx(
    "min-w-[880px]",
    "[&_tbody_tr]:cursor-pointer [&_tbody_tr:hover_td]:bg-grey-0 [&_tbody_tr[data-open=true]_td]:bg-primary-wash",
    "[&_td:nth-child(2)_strong]:whitespace-nowrap",
    "[&_.sc-open-link]:text-[13px]",
  ),

  offer: cx(
    "grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-3 overflow-hidden",
    "[&_.sc-cost-metrics]:grid-cols-4 [&_.sc-cost-metrics]:gap-3 max-[900px]:[&_.sc-cost-metrics]:grid-cols-2",
    "[&_.sc-card]:gap-2 [&_.sc-card]:p-3",
    "[&_.sc-bars]:h-full [&_.sc-bars]:min-h-0 [&_.sc-bars]:flex-1",
    "[&_.sc-bars-plot]:min-h-0 [&_.sc-bars-plot]:flex-1",
    "[&_.sc-bars-axis]:h-full [&_.sc-bars-axis]:min-h-24",
    "[&_.sc-bars-grid]:h-full [&_.sc-bars-grid]:min-h-24",
    "max-[900px]:[&_.sc-metrics]:grid-cols-2",
  ),
  offerMain: "grid min-h-0 grid-cols-[minmax(0,1.15fr)_minmax(240px,0.85fr)] gap-3 max-[900px]:grid-cols-1",
  offerCost: "min-h-0 gap-2 overflow-auto p-3",
  offerSide: cx(
    "flex min-h-0 flex-col gap-3 overflow-hidden",
    "[&_.sc-panel]:min-h-0 [&_.sc-panel]:flex-1 [&_.sc-panel]:gap-2 [&_.sc-panel]:p-3",
  ),
  outcome: "min-h-0 overflow-hidden",
  homeFacts: cx(
    "m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm",
    "[&_dt]:text-ink-muted [&_dd]:m-0 [&_dd]:text-ink",
  ),
  quoteNote: "m-0 text-[13px] text-ink-muted",
  quoteQtys: cx(
    "flex items-center gap-2 text-[13px] text-ink-muted",
    "[&_input]:h-9 [&_input]:w-[140px] [&_input]:rounded-sm [&_input]:border [&_input]:border-grey-100 [&_input]:px-2 [&_input]:text-sm [&_input]:[font:inherit]",
  ),
  quoteMargin: cx(
    "grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] items-end gap-3",
    "[&_label]:flex [&_label]:flex-col [&_label]:gap-1 [&_label]:text-xs [&_label]:font-medium [&_label]:text-ink-muted",
    "[&_input]:h-10 [&_input]:rounded [&_input]:border [&_input]:border-grey-100 [&_input]:px-3 [&_input]:text-base [&_input]:text-ink [&_input]:[font:inherit]",
    "[&_output]:text-[22px] [&_output]:font-semibold [&_output]:tracking-[-0.02em]",
  ),
  quoteReason: "m-0 border-l-2 border-primary bg-grey-0 px-3 py-2 text-[13px] text-ink",
  quoteActions: "flex flex-wrap items-center gap-2",
  quoteFollowup:
    "flex flex-wrap items-center justify-between gap-2 rounded border border-warning bg-warning-wash p-3 text-sm",
  quoteLate: "font-semibold text-danger",
  quoteFits: "font-semibold text-success-ink",

  // Quote review: one block per line, uptool-style op cards with cost columns.
  review: "flex flex-col gap-3",
  reviewLine: "flex flex-col gap-2 rounded-lg border border-grey-100 bg-white p-3",
  reviewLineHead: cx(
    "flex flex-col gap-1",
    "[&_strong]:text-sm [&_strong]:font-semibold [&_strong]:text-ink [&_span]:text-xs [&_span]:text-ink-muted",
  ),
  opCard: "flex flex-col gap-2 rounded-lg border border-grey-100 bg-grey-0 p-3",
  opMainCol: "flex min-w-0 flex-col gap-2",
  opHead: "flex flex-wrap items-center gap-2 [&_strong]:text-sm [&_strong]:font-semibold [&_strong]:text-ink",
  opMode: cx(
    "inline-flex h-7 items-center rounded border border-grey-100 bg-white px-2 text-xs text-ink",
    "[&_select]:cursor-pointer [&_select]:border-0 [&_select]:bg-transparent [&_select]:text-xs [&_select]:text-inherit [&_select]:[font:inherit]",
  ),
  opRemove: cx(
    "ml-auto inline-flex size-7 cursor-pointer items-center justify-center rounded border border-grey-100 bg-white text-base leading-none text-ink-muted",
    "hover:text-danger disabled:cursor-default disabled:opacity-50",
  ),
  opFields: "flex flex-wrap gap-2",
  opField: cx(
    "flex w-[104px] flex-col gap-0.5 rounded border border-grey-100 bg-white px-2 py-1 text-[11px] leading-tight text-ink-muted",
    "[&_input]:h-6 [&_input]:w-full [&_input]:border-0 [&_input]:bg-transparent [&_input]:p-0 [&_input]:text-sm [&_input]:text-ink [&_input]:[font:inherit]",
    "[&_input:focus-visible]:outline-none focus-within:border-primary",
    "[&_input:disabled]:text-ink-muted [&_em]:text-[10px] [&_em]:not-italic",
  ),
  opCosts: cx(
    "flex flex-wrap justify-end gap-x-4 gap-y-1 text-right font-mono text-[11px] leading-tight text-ink-muted tabular-nums",
    "[&>div]:min-w-[88px] [&_b]:block [&_b]:text-sm [&_b]:font-semibold [&_b]:text-ink [&_span]:block [&_span]:whitespace-nowrap",
  ),
} as const;
